/**
 * useOperationsFeed — Shared hook for the live ticker + pressure panel.
 * --------------------------------------------------------------------
 * Pulls `/api/operations/feed` on mount, refreshes every 60s (restrained
 * cadence — no flashing), and re-fetches on every `forgeops:submission`
 * event with a small backend-write delay.
 *
 * Returns:
 *   {
 *     events:    [ { id, kind, sev, src, line, ts } ],   // newest first
 *     pressure:  { open_incidents, equipment_lockouts, ... },
 *     seeded:    boolean   // true when backend fell back to seed events
 *   }
 *
 * Aesthetic contract: when DB has real records, the seeded array is
 * silently replaced. No popping, no jumping, no visual hand-off cue —
 * the operator should not be able to tell the difference between
 * seeded and live data unless they look at the timestamps.
 */
import { useEffect, useRef, useState } from "react";

const REFRESH_INTERVAL_MS   = 60_000;
const POST_SUBMIT_DELAY_MS  = 700;
const API_BASE              = process.env.REACT_APP_BACKEND_URL || "";

export function useOperationsFeed(limit = 12) {
  const [data, setData] = useState({
    events:   [],
    pressure: null,
    seeded:   true,
    loaded:   false,
  });
  const aborted = useRef(false);

  useEffect(() => {
    aborted.current = false;
    let intervalId   = null;
    let submitTimer  = null;

    const fetchFeed = async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/operations/feed?limit=${limit}`,
          { headers: { "Content-Type": "application/json" } },
        );
        if (!r.ok) return;
        const json = await r.json();
        if (aborted.current) return;
        setData({
          events:   Array.isArray(json.events) ? json.events : [],
          pressure: json.pressure || null,
          seeded:   Boolean(json.seeded),
          loaded:   true,
        });
      } catch (_e) {
        // Network blip — keep the last known state silently.
      }
    };

    fetchFeed();
    intervalId = setInterval(fetchFeed, REFRESH_INTERVAL_MS);

    const onSubmission = () => {
      if (submitTimer) clearTimeout(submitTimer);
      submitTimer = setTimeout(fetchFeed, POST_SUBMIT_DELAY_MS);
    };
    window.addEventListener("forgeops:submission", onSubmission);

    return () => {
      aborted.current = true;
      if (intervalId) clearInterval(intervalId);
      if (submitTimer) clearTimeout(submitTimer);
      window.removeEventListener("forgeops:submission", onSubmission);
    };
  }, [limit]);

  return data;
}
